import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '../../components/ui/PageContainer/PageContainer';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import {
  platformAIConfigService,
  type PlatformAIConfig,
  type PlatformAIUsageSummary,
} from '@/services/PlatformAIConfigService';

import { apiFetch } from '@/lib/api';
import { AIProviderForm, type AIProviderFormData, type EditableConfig, type ProviderInfo } from '@/components/ai/AIProviderForm';
import { PROVIDER_LABELS, PROVIDER_ICONS } from '@/components/ai/aiProviderConstants';

// PROVIDER_LABELS, PROVIDER_ICONS, ProviderInfo, AIProviderForm all live in
// `@/components/ai/*` — shared with src/routes/organization/AISettings.tsx
// (org-scoped panel). Sven (2026-04-26): "Please do not copy
// components...REUSE THEM!"

async function fetchJSON(url: string, opts?: RequestInit) {
  // FW8: route through apiFetch — consistency with the org panel.
  const res = await apiFetch(url, opts);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Request failed');
  return data.data;
}

export function PlatformAISettings({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<EditableConfig | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

  // Queries
  const { data: configs = [], isLoading } = useQuery<PlatformAIConfig[]>({
    queryKey: ['platform-ai-configs'],
    queryFn: () => platformAIConfigService.listConfigs(),
  });

  const { data: providers = [] } = useQuery<ProviderInfo[]>({
    queryKey: ['ai-providers'],
    queryFn: () => fetchJSON('/api/ai-config/providers'),
  });

  const { data: usage = [] } = useQuery<PlatformAIUsageSummary[]>({
    queryKey: ['platform-ai-usage'],
    queryFn: () => platformAIConfigService.getUsageSummary(30),
  });

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: AIProviderFormData) => {
      if (editingConfig) {
        return platformAIConfigService.updateConfig(editingConfig.id, data);
      } else {
        return platformAIConfigService.createConfig(data as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-ai-configs'] });
      // BUG E1 fix: drop stale cached model lists so the next Edit open refetches
      // live models using the now-saved key.
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => platformAIConfigService.deleteConfig(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-ai-configs'] }),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => platformAIConfigService.testConnection(id),
    onSuccess: (result, id) => setTestResult({ id, ...result }),
  });

  function resetForm() {
    setShowAddForm(false);
    setEditingConfig(null);
  }

  function startEdit(config: PlatformAIConfig) {
    setEditingConfig({
      id: config.id,
      provider: config.provider,
      modelPreference: config.modelPreference,
      modelComplex: config.modelComplex,
      baseUrl: config.baseUrl,
      monthlyBudget: config.monthlyBudget,
      enabled: config.enabled,
    });
    setShowAddForm(true);
  }

  // Available providers not yet configured
  const configuredProviders = configs.map((c) => c.provider);
  const availableProviders = providers.filter((p) => !configuredProviders.includes(p.provider));

  const content = (
      <div className={embedded ? '' : 'max-w-4xl'} data-no-translate>
        {!embedded && (
          <>
            <h1 className="text-2xl font-bold text-foreground mb-2">Platform AI & LLM Configuration</h1>
            <p className="text-muted-foreground mb-8">
              Configure platform-level AI provider API keys. These serve as defaults for all organizations that don't have their own keys configured. Keys are encrypted at rest and never exposed.
            </p>
          </>
        )}

        {/* Usage Summary */}
        {usage.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Platform Usage (Last 30 Days)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {usage.map((u) => (
                <div key={u.provider} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{PROVIDER_ICONS[u.provider] || '\u{1F52E}'}</span>
                    <span className="font-medium text-foreground">{PROVIDER_LABELS[u.provider] || u.provider}</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">${u.totalCostUsd.toFixed(4)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {u.totalCalls} calls &middot; {((u.totalInputTokens + u.totalOutputTokens) / 1000).toFixed(1)}K tokens
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Configured Providers */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Configured Providers</h2>
            {availableProviders.length > 0 && !showAddForm && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingConfig(null);
                  setShowAddForm(true);
                }}
              >
                + Add Provider
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : configs.length === 0 && !showAddForm ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <div className="text-4xl mb-4">{'\u{1F511}'}</div>
              <h3 className="text-lg font-medium text-foreground mb-2">No platform AI providers configured</h3>
              <p className="text-muted-foreground mb-4">
                Add platform-level API keys to serve as defaults for organizations without their own keys.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                Add Your First Provider
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{PROVIDER_ICONS[config.provider] || '\u{1F52E}'}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">
                            {PROVIDER_LABELS[config.provider] || config.provider}
                          </h3>
                          <Badge variant={config.enabled ? 'success' : 'secondary'}>
                            {config.enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                          Key: <Badge variant="outline" className="font-mono text-xs">{config.apiKeyHint}</Badge>
                          {config.modelPreference && (
                            <> &middot; Model: <Badge variant="outline" className="font-mono text-xs">{config.modelPreference}</Badge></>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => testMutation.mutate(config.id)}
                        loading={testMutation.isPending}
                      >
                        Test
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(config)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm('Remove this platform AI provider configuration?')) {
                            deleteMutation.mutate(config.id);
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  {/* Budget info */}
                  {config.monthlyBudget && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Monthly Budget</span>
                        <span className="text-foreground font-medium">
                          ${config.currentSpend.toFixed(2)} / ${config.monthlyBudget.toFixed(2)}
                        </span>
                      </div>
                      <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (config.currentSpend / config.monthlyBudget) >= 1
                              ? 'bg-red-500'
                              : (config.currentSpend / config.monthlyBudget) >= 0.8
                              ? 'bg-amber-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, (config.currentSpend / config.monthlyBudget) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Test result */}
                  {testResult && testResult.id === config.id && (
                    <div className={`mt-3 p-3 rounded-md text-sm ${
                      testResult.success
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {testResult.success ? '\u2705' : '\u274C'} {testResult.message}
                    </div>
                  )}

                  {/* Capabilities */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(config.capabilities).map(([cap, enabled]) => (
                      <Badge
                        key={cap}
                        variant={enabled ? 'default' : 'secondary'}
                        className={enabled ? '' : 'line-through opacity-60'}
                      >
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Form — shared with org-scoped AISettings (no duplication) */}
        {showAddForm && (
          <>
            <AIProviderForm
              mode="platform"
              providers={providers as ProviderInfo[]}
              availableProviders={availableProviders as ProviderInfo[]}
              editingConfig={editingConfig}
              onSubmit={async (data) => { await saveMutation.mutateAsync(data); }}
              onCancel={resetForm}
              isSaving={saveMutation.isPending}
            />
            {saveMutation.isError && (
              <div className="p-3 mb-8 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
                {(saveMutation.error as Error).message}
              </div>
            )}
          </>
        )}

        {/* Info box */}
        <div className="bg-card border border-border rounded-lg p-5">
          <h3 className="font-semibold text-foreground mb-2">How platform AI configuration works</h3>
          <ul className="text-sm text-muted-foreground space-y-1.5">
            <li>• <strong>Credential resolution chain</strong>: Organization config → Platform DB config → Environment variables</li>
            <li>• <strong>Platform defaults</strong>: These keys are used when an organization hasn't configured its own provider keys</li>
            <li>• <strong>Keys are encrypted</strong>: AES-256-GCM at rest, decrypted only in memory during API calls</li>
            <li>• <strong>Budget controls</strong>: Set monthly limits per provider. When exceeded, falls back to env vars</li>
            <li>• <strong>Test before saving</strong>: Verify your key works with a quick connection test</li>
            <li>• <strong>Usage tracking</strong>: Platform-level calls (where no org is set) are tracked separately</li>
          </ul>
        </div>
      </div>
  );

  if (embedded) return content;

  return (
    <PageContainer>
      {content}
    </PageContainer>
  );
}
